import React, { useEffect, useMemo, useRef, useState } from 'react';

const WORKBOARD_API = 'https://cms.amservices.eu/api/board_notes.php';

const ToolFrame = ({ url, theme = 'dark', searchSelection }) => {
    const [loading, setLoading] = useState(true);
    const iframeRef = useRef(null);
    const initialTheme = useRef(theme);
    const mountCacheBuster = useRef(Date.now());
    const selectionRef = useRef(searchSelection);
    selectionRef.current = searchSelection;
    useEffect(() => {
        const origin = new URL(url).origin;
        const sendSelection = () => {
            if (selectionRef.current) iframeRef.current?.contentWindow?.postMessage({ type: 'AMS_SEARCH_SELECT', ...selectionRef.current }, origin);
        };
        const receive = event => {
            if (event.source !== iframeRef.current?.contentWindow || event.origin !== origin) return;
            if (event.data?.type === 'AMS_SEARCH_OPEN') window.dispatchEvent(new Event('ams-open-global-search'));
            if (event.data?.type === 'AMS_SEARCH_READY') sendSelection();
        };
        window.addEventListener('message', receive);
        return () => window.removeEventListener('message', receive);
    }, [url]);
    useEffect(() => {
        if (searchSelection) iframeRef.current?.contentWindow?.postMessage({ type: 'AMS_SEARCH_SELECT', ...searchSelection }, new URL(url).origin);
    }, [searchSelection, url]);
    const userStr = localStorage.getItem('am_user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userLogin = String(user?.login || user?.user || user?.username || '').trim().toLowerCase();
    const supportsSharedTheme =
        url.includes('planning.amservices.eu') ||
        url.includes('appel.amservices.eu') ||
        url.includes('cdr.amservices.eu') ||
        url.includes('commercial.amservices.eu') ||
        url.includes('backup.amservices.eu') ||
        url.includes('suivi.amservices.eu') ||
        url.includes('stock.amservices.eu') ||
        url.includes('://client.amservices.eu') ||
        url.includes('://comcom.client.amservices.eu') ||
        url.includes('://sms.amservices.eu') ||
        url.includes('kanban.amservices.eu');

    // L'URL reste stable pendant toute la durée de vie de l'iframe.
    // Cela évite un rechargement après chaque onLoad.
    const iframeUrl = useMemo(() => {
        let resolvedUrl;
        if (url.includes('aurelien.kanban.amservices.eu')) {
            resolvedUrl = 'https://aurelien.kanban.amservices.eu/suivi-production?secret=AMS_SECRET_CONNEXION_2026';
        } else if (url.includes('aymeric.kanban.amservices.eu')) {
            resolvedUrl = 'https://aymeric.kanban.amservices.eu/suivi-production?secret=AMS_SECRET_CONNEXION_2026';
        } else if (url.includes('kanban.amservices.eu')) {
            resolvedUrl = 'https://kanban.amservices.eu/suivi-production?secret=AMS_SECRET_CONNEXION_2026';
        } else if (
            url.includes('backup') ||
            url.includes('client') ||
            url.includes('comcom') ||
            url.includes('sms') ||
            url.includes('cdr.amservices.eu') ||
            url.includes('commercial.amservices.eu')
        ) {
            const separator = url.includes('?') ? '&' : '?';
            resolvedUrl = `${url}${separator}secret=AMS_SECRET_CONNEXION_2026`;
        } else {
            const separator = url.includes('?') ? '&' : '?';
            resolvedUrl = `${url}${separator}token=${encodeURIComponent(userLogin)}`;
        }

        if (supportsSharedTheme) {
            const separator = resolvedUrl.includes('?') ? '&' : '?';
            const iframeTheme = url.includes('backup.amservices.eu') ||
                url.includes('suivi.amservices.eu') ||
                url.includes('stock.amservices.eu') ||
                url.includes('kanban.amservices.eu')
                ? theme
                : initialTheme.current;
            resolvedUrl = `${resolvedUrl}${separator}theme=${iframeTheme}`;
        }

        if (userLogin === 'tableau' && url.includes('appel.amservices.eu/affichage')) {
            resolvedUrl = `${resolvedUrl}&refresh=${mountCacheBuster.current}`;
        }

        return resolvedUrl;
    }, [url, userLogin, supportsSharedTheme, theme]);

    useEffect(() => {
        setLoading(true);
    }, [url]);

    useEffect(() => {
        if (!supportsSharedTheme) return;
        iframeRef.current?.contentWindow?.postMessage({ type: 'ams-theme', theme }, '*');
    }, [theme, supportsSharedTheme]);

    useEffect(() => {
        if (!url.includes('kanban.amservices.eu')) return undefined;
        const receiveKanbanTask = async (event) => {
            if (event.source !== iframeRef.current?.contentWindow || event.data?.type !== 'AMS_WORKBOARD_IMPORT') return;
            const task = event.data.task || {};
            const reply = (success, message) => event.source?.postMessage(
                { type: 'AMS_WORKBOARD_IMPORT_RESULT', requestId: event.data.requestId, success, message },
                event.origin || '*'
            );
            if (!task.id) {
                reply(false, 'Tâche Kanban invalide.');
                return;
            }
            const token = localStorage.getItem('am_token') || '';
            if (!token) {
                reply(false, 'Session CMS manquante. Reconnectez-vous.');
                return;
            }
            const details = [
                task.client_nom ? `Client : ${task.client_nom}` : '',
                task.stage_title ? `Étape Kanban : ${task.stage_title}` : '',
                task.categorie ? `Catégorie : ${task.categorie}` : '',
                task.num_devis ? `Devis : ${task.num_devis}` : '',
                task.responsable_nom ? `Responsable Kanban : ${task.responsable_nom}` : '',
                Number(task.is_urgent) === 1 ? 'URGENT' : '',
                task.notes || '',
            ].filter(Boolean).join('\n');
            try {
                const response = await fetch(WORKBOARD_API, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                        'X-Auth-Token': token,
                    },
                    body: JSON.stringify({
                        action: 'pin_create',
                        title: [task.client_nom, task.objet_inter].filter(Boolean).join(' — ') || 'Tâche Kanban',
                        note: details,
                        kind: 'CHANTIER',
                        assigned_to: '',
                        status: ['103', '204', '300'].includes(String(task.id_etape)) ? 'IN_PROGRESS' : 'TODO',
                        deadline: task.date_echeance ? `${String(task.date_echeance).slice(0, 10)}T18:00` : '',
                        position_order: Date.now() % 100000,
                        source_type: 'kanban',
                        source_id: String(task.id),
                        source_url: `https://kanban.amservices.eu/suivi-production?suivi=${encodeURIComponent(task.id)}`,
                    }),
                });
                const data = await response.json();
                if (!response.ok || !data.success) throw new Error(data.message || 'Envoi au Workboard impossible');
                reply(true, `« ${task.client_nom || task.objet_inter || 'La tâche'} » a été ajoutée aux pense-bêtes équipe.`);
            } catch (error) {
                reply(false, error.message || 'Envoi au Workboard impossible');
            }
        };
        window.addEventListener('message', receiveKanbanTask);
        return () => window.removeEventListener('message', receiveKanbanTask);
    }, [url]);

    return (
        <div className="iframe-wrapper">
            {loading && (
                <div style={styles.loader}>
                    <p>Chargement de l'outil...</p>
                </div>
            )}
            <iframe
                ref={iframeRef}
                src={iframeUrl}
                className="iframe-content"
                title="Tool Frame"
                onLoad={() => {
                    setLoading(false);
                    if (selectionRef.current) iframeRef.current?.contentWindow?.postMessage({ type: 'AMS_SEARCH_SELECT', ...selectionRef.current }, new URL(url).origin);
                    if (supportsSharedTheme) {
                        iframeRef.current?.contentWindow?.postMessage({ type: 'ams-theme', theme }, '*');
                    }
                }}
                allowFullScreen
                allow="clipboard-write; clipboard-read; fullscreen; accelerometer; gyroscope; camera; microphone"
            />
        </div>
    );
};

const styles = {
    loader: {
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: '#ffffff', zIndex: 10
    }
};

export default ToolFrame;
